import { environment } from '../../../environments/environment';
import { type Country } from '../../modules/master-data/countries/country.model';

/**
 * Mobile numbers are stored in one form, E.164 (`+919845011223`), and entered as a country code picked from a
 * dropdown plus the national number (October 2026, TDD 16). These helpers go between the two.
 */

/** A country's telephone code as digits, without `+` (the countries list may store either). */
export function dialCode(country: Country | null | undefined): string {
  const code = country?.telephoneCode ?? '';
  return code.startsWith('+') ? code.slice(1) : code;
}

/** The country the dropdown starts on: `environment.defaultCountryCode` (ISO, e.g. `IN`), else the first. */
export function defaultDialCountry(countries: Country[]): Country | null {
  return countries.find(c => c.code === environment.defaultCountryCode) ?? countries[0] ?? null;
}

/**
 * A stored E.164 number back as the dropdown's code and the national number. Country codes are prefix-free,
 * so at most one of the listed codes can match. A value without `+` (or with an unlisted code) comes back as
 * the number, with no code.
 */
export function splitE164(value: string | null | undefined, countries: Country[]): { dialCode: string | null; national: string } {
  const v = (value ?? '').trim();
  if (!v.startsWith('+')) {
    return { dialCode: null, national: v };
  }
  const digits = v.slice(1);
  const match = countries.map(dialCode).find(code => code && digits.startsWith(code));
  return match ? { dialCode: match, national: digits.slice(match.length) } : { dialCode: null, national: v };
}

/** `+919845011223` as `+91 9845011223`, for showing a stored number; anything else unchanged. */
export function formatE164(value: string | null | undefined, countries: Country[]): string {
  const { dialCode: code, national } = splitE164(value, countries);
  return code ? `+${code} ${national}` : (value ?? '');
}

/** The flag emoji for an ISO 3166-1 alpha-2 code. */
export function flagEmoji(iso2: string | undefined): string {
  const ISO_ALPHA2_LENGTH = 2;
  const REGIONAL_INDICATOR_OFFSET = 127397;
  if (!iso2 || iso2.length !== ISO_ALPHA2_LENGTH) return '';
  return iso2
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(c.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET))
    .join('');
}
